import { useCallback, useRef, useState, type DragEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useReqdeskContext } from '../ReqdeskProvider'
import { widgetTicketDetailOptions } from '../queries'
import { submitReply, closeTicket, uploadReplyAttachment, type AttachmentMeta, type TicketDetail } from '../../api-client'
import { en } from '../../i18n/en'
import { ar } from '../../i18n/ar'
import { FormErrorProvider, FormFieldGroup } from '../form-errors'
import { normalizeMentionsForDisplay } from '../mentions'
import { formatFileSize, queuedFileId, validateAttachment, type QueuedFile } from './attachments'

const translations: Record<string, Record<string, string>> = { en, ar }

/** Cap reply attachments lower than the ticket-level cap — replies are inline edits, not the
 *  primary context-delivery vehicle; keeps the UI tight and matches the backend per-reply limit. */
const MAX_REPLY_FILES = 3

interface TicketDetailViewProps {
  ticketId: string
  onBack: () => void
}

function formatDate(iso: string): string {
  try { return new Date(iso).toLocaleString() } catch { return iso }
}

/**
 * Short, human-friendly time label for reply cards. Uses relative copy for recent replies
 * (≤ 59s, ≤ 59m, ≤ 23h) and absolute date + time thereafter. The full timestamp is still
 * exposed via `title` for users who need the exact value.
 */
function formatReplyTime(iso: string, locale: string): { label: string; title: string } {
  if (!iso) return { label: '', title: '' }
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return { label: iso, title: iso }
  const now = Date.now()
  const diffSec = Math.floor((now - parsed.getTime()) / 1000)
  const title = parsed.toLocaleString()
  if (diffSec < 60) return { label: 'just now', title }
  if (diffSec < 3600) return { label: `${Math.floor(diffSec / 60)}m ago`, title }
  if (diffSec < 86400) return { label: `${Math.floor(diffSec / 3600)}h ago`, title }
  const sameYear = parsed.getFullYear() === new Date(now).getFullYear()
  const label = parsed.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    year: sameYear ? undefined : 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
  return { label, title }
}

/** First letter of the author name (or `@` of email-like strings) as an avatar-safe initial. */
function initialsFor(name: string): string {
  const trimmed = (name ?? '').trim()
  if (!trimmed) return '?'
  const firstChar = trimmed.replace(/^[^\p{L}\p{N}]+/u, '').charAt(0)
  return (firstChar || '?').toUpperCase()
}

export function TicketDetailView({ ticketId, onBack }: TicketDetailViewProps) {
  const ctx = useReqdeskContext()
  const queryClient = useQueryClient()
  const [replyBody, setReplyBody] = useState('')
  const [originalOpen, setOriginalOpen] = useState(false)

  // Reply-attachment queue + drag-drop state. Files stage locally, validate against the shared
  // attachment policy, and upload sequentially after the reply POST succeeds — same pattern
  // SubmitTicketView uses for the initial ticket. Progress is optional and non-blocking.
  const [replyFiles, setReplyFiles] = useState<QueuedFile[]>([])
  const [replyDragOver, setReplyDragOver] = useState(false)
  const [replyUploadProgress, setReplyUploadProgress] = useState<{ fileName: string; percent: number } | null>(null)
  const replyFileInputRef = useRef<HTMLInputElement>(null)

  const t = useCallback((key: string): string => {
    if (ctx.translations?.[key]) return ctx.translations[key]
    const lang = translations[ctx.language] ?? translations.en
    return lang[key] ?? key
  }, [ctx.language, ctx.translations])

  const { data: ticket, isLoading, error } = useQuery(widgetTicketDetailOptions(ticketId))

  const replyMutation = useMutation({
    // When the backend starts returning per-field validation (e.g. min body length), the
    // global handler stays out of 422 territory and the <FormErrorProvider> below renders
    // inline messages. Non-422 failures (network, 5xx) still toast via the global cache.
    meta: { form: 'reply-send' as const },
    mutationFn: async ({ body, validFiles }: { body: string; validFiles: QueuedFile[] }) => {
      const result = await submitReply(ticketId, body)
      // Upload queued reply attachments sequentially so the progress indicator reflects a single
      // active file at a time. Failures are caught so a single bad file doesn't orphan the reply
      // — the user still sees the reply land, and the attachment error surfaces via the global
      // toast through the uploadWithProgress rejection path.
      for (const queued of validFiles) {
        setReplyUploadProgress({ fileName: queued.file.name, percent: 0 })
        try {
          await uploadReplyAttachment(ticketId, result.id, queued.file, (percent) => {
            setReplyUploadProgress({ fileName: queued.file.name, percent })
          })
        } catch { /* surfaced globally; skip and continue */ }
      }
      setReplyUploadProgress(null)
      return result
    },
    onMutate: async ({ body }) => {
      // Optimistic update: append reply to cache
      await queryClient.cancelQueries({ queryKey: ['widget-ticket', ticketId] })
      const previous = queryClient.getQueryData<TicketDetail>(['widget-ticket', ticketId])

      if (previous) {
        queryClient.setQueryData<TicketDetail>(['widget-ticket', ticketId], {
          ...previous,
          replies: [...previous.replies, {
            id: `optimistic-${Date.now()}`,
            body,
            authorName: ctx.userName ?? ctx.userEmail ?? 'You',
            isStaff: false,
            createdAt: new Date().toISOString(),
          }],
        })
      }
      return { previous }
    },
    onSuccess: () => {
      setReplyBody('')
      setReplyFiles([])
      setReplyUploadProgress(null)
      queryClient.invalidateQueries({ queryKey: ['widget-ticket', ticketId] })
    },
    onError: (_err, _vars, context) => {
      setReplyUploadProgress(null)
      if (context?.previous) {
        queryClient.setQueryData(['widget-ticket', ticketId], context.previous)
      }
    },
  })

  function addReplyFiles(newFiles: FileList | File[]) {
    const toAdd: QueuedFile[] = []
    for (const file of Array.from(newFiles)) {
      if (replyFiles.length + toAdd.length >= MAX_REPLY_FILES) break
      const { error } = validateAttachment(file, t)
      toAdd.push({ file, id: queuedFileId(), error: error ?? undefined })
    }
    setReplyFiles(prev => [...prev, ...toAdd])
  }

  function removeReplyFile(id: string) {
    setReplyFiles(prev => prev.filter(f => f.id !== id))
  }

  function handleReplyDragOver(e: DragEvent) { e.preventDefault(); setReplyDragOver(true) }
  function handleReplyDragLeave(e: DragEvent) { e.preventDefault(); setReplyDragOver(false) }
  function handleReplyDrop(e: DragEvent) {
    e.preventDefault(); setReplyDragOver(false)
    if (e.dataTransfer.files.length > 0) addReplyFiles(e.dataTransfer.files)
  }

  function handleSendReply() {
    const body = replyBody.trim()
    if (!body) return
    const validFiles = replyFiles.filter(f => !f.error)
    replyMutation.mutate({ body, validFiles })
  }

  // No `meta.form` — the resolve button has no form-field layout, so a 422 (if the backend ever
  // returns one) should still surface as a toast via the global MutationCache handler.
  const resolveMutation = useMutation({
    mutationFn: () => closeTicket(ticketId),
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['widget-ticket', ticketId] })
      const previous = queryClient.getQueryData<TicketDetail>(['widget-ticket', ticketId])
      if (previous) {
        queryClient.setQueryData<TicketDetail>(['widget-ticket', ticketId], {
          ...previous,
          status: 'resolved',
        })
      }
      return { previous }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['widget-ticket', ticketId] })
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['widget-ticket', ticketId], context.previous)
      }
    },
  })

  if (isLoading) {
    return <div className="rqd-loading">{t('detail.loading')}</div>
  }

  if (error || !ticket) {
    return (
      <div className="rqd-placeholder">
        <p>{t('error.generic')}</p>
        <button className="rqd-btn rqd-btn-secondary" style={{ width: 'auto', padding: '8px 20px' }} onClick={onBack}>
          {t('tracker.back')}
        </button>
      </div>
    )
  }

  const isActive = ticket.status !== 'resolved' && ticket.status !== 'closed'

  return (
    <div className="rqd-detail">
      {/* ─── Main column: title, description, conversation, composer ─── */}
      <div className="rqd-detail-main">
        <div className="rqd-detail-headline">
          <h3 className="rqd-detail-title">{ticket.title}</h3>
          <div className="rqd-detail-meta-row">
            <span className="rqd-ticket-card-number">{ticket.ticketNumber}</span>
            <span className="rqd-badge">{ticket.status}</span>
            <span className="rqd-badge rqd-badge-muted">{ticket.priority}</span>
            {ticket.tags.length > 0 && ticket.tags.map(tag => (
              <span
                key={tag.id}
                className="rqd-tag-chip rqd-tag-chip-readonly"
                style={{ ['--rqd-tag-color' as string]: tag.color }}
              >
                <span className="rqd-tag-chip-dot" aria-hidden="true" />
                {tag.name}
              </span>
            ))}
          </div>
        </div>

        {(ticket.description || ticket.attachments.length > 0) && (
          <div className={`rqd-detail-original${originalOpen ? ' rqd-open' : ''}`}>
            <button
              type="button"
              className="rqd-detail-original-summary"
              onClick={() => setOriginalOpen(v => !v)}
              aria-expanded={originalOpen}
            >
              <span className="rqd-detail-original-label">
                {t('detail.originalRequest') ?? 'Original request'}
              </span>
              {ticket.attachments.length > 0 && (
                <span className="rqd-detail-original-count">
                  {ticket.attachments.length} {ticket.attachments.length === 1 ? 'file' : 'files'}
                </span>
              )}
              <svg className="rqd-detail-original-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            <div className="rqd-detail-original-body">
              {ticket.description && (
                <div className="rqd-ticket-desc">{ticket.description}</div>
              )}
              {ticket.attachments.length > 0 && (
                <div className="rqd-detail-attachments-inline">
                  <div className="rqd-attachment-list">
                    {ticket.attachments.map((a: AttachmentMeta) => (
                      <div key={a.id} className="rqd-attachment-item">
                        <div className="rqd-file-item-info">
                          <span className="rqd-file-item-name">{a.fileName}</span>
                          <span className="rqd-file-item-size">{formatFileSize(a.fileSize)}</span>
                        </div>
                        {a.downloadUrl && (
                          <a href={a.downloadUrl} target="_blank" rel="noopener noreferrer">
                            {t('attach.download')}
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="rqd-section-title">{t('detail.replies')}</div>
        {ticket.replies.length === 0 ? (
          <p className="rqd-detail-empty">{t('detail.noReplies')}</p>
        ) : (
          <div className="rqd-conversation">
            {ticket.replies.map(reply => {
              const time = formatReplyTime(reply.createdAt, ctx.language)
              return (
                <div
                  key={reply.id}
                  className={`rqd-bubble ${reply.isStaff ? 'rqd-bubble-staff' : 'rqd-bubble-customer'}`}
                >
                  <div className="rqd-bubble-header">
                    <span className="rqd-bubble-avatar" aria-hidden="true">{initialsFor(reply.authorName)}</span>
                    <span className="rqd-bubble-author">{reply.authorName}</span>
                    {reply.isStaff && <span className="rqd-bubble-staff-badge">{t('detail.staff')}</span>}
                    <span className="rqd-bubble-time" title={time.title}>{time.label}</span>
                  </div>
                  <div className="rqd-bubble-body">{normalizeMentionsForDisplay(reply.body)}</div>
                  {reply.attachments && reply.attachments.length > 0 && (
                    <div className="rqd-bubble-attachments">
                      {reply.attachments.map((a) => (
                        <div key={a.id} className="rqd-bubble-attachment">
                          <span className="rqd-bubble-attachment-icon" aria-hidden="true">{'\u{1F4CE}'}</span>
                          <div className="rqd-file-item-info">
                            <span className="rqd-file-item-name">{a.fileName}</span>
                            <span className="rqd-file-item-size">{formatFileSize(a.fileSize)}</span>
                          </div>
                          {a.downloadUrl && (
                            <a href={a.downloadUrl} target="_blank" rel="noopener noreferrer">
                              {t('attach.download')}
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        <FormErrorProvider mutation={replyMutation}>
          <div className="rqd-reply-compose">
            <FormFieldGroup name="body">
              <textarea
                className="rqd-textarea"
                name="body"
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder={t('detail.replyPlaceholder')}
                rows={3}
              />
            </FormFieldGroup>

            {/* Reply-attachment dropzone. Parallels SubmitTicketView's initial-attach UI so
                users have one consistent mental model for adding files. */}
            <div
              className={`rqd-dropzone rqd-dropzone-compact${replyDragOver ? ' rqd-dropzone-active' : ''}`}
              onDragOver={handleReplyDragOver}
              onDragLeave={handleReplyDragLeave}
              onDrop={handleReplyDrop}
              onClick={() => replyFileInputRef.current?.click()}
              role="button"
              tabIndex={0}
              aria-label={t('detail.replyAttachLabel')}
            >
              <span className="rqd-dropzone-icon" aria-hidden="true">{'\u{1F4CE}'}</span>
              <span>{replyDragOver ? t('attach.dropzoneActive') : t('detail.replyAttachHint')}</span>
              <input
                ref={replyFileInputRef}
                type="file"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => { if (e.target.files) addReplyFiles(e.target.files); e.target.value = '' }}
              />
            </div>

            {replyFiles.length > 0 && (
              <div className="rqd-file-list">
                {replyFiles.map(f => (
                  <div key={f.id} className={`rqd-file-item${f.error ? ' rqd-file-item-error' : ''}`}>
                    <div className="rqd-file-item-info">
                      <span className="rqd-file-item-name">{f.file.name}</span>
                      <span className="rqd-file-item-size">
                        {formatFileSize(f.file.size)}
                        {f.error ? ` \u00b7 ${f.error}` : ''}
                      </span>
                    </div>
                    <button
                      type="button"
                      className="rqd-file-remove"
                      onClick={() => removeReplyFile(f.id)}
                      aria-label={t('attach.remove')}
                    >
                      {'\u00d7'}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {replyUploadProgress && (
              <div className="rqd-upload-status">
                {t('attach.uploading')} — {replyUploadProgress.fileName} · {replyUploadProgress.percent}%
                <div className="rqd-progress" style={{ marginTop: 4 }}>
                  <div className="rqd-progress-bar" style={{ width: `${replyUploadProgress.percent}%` }} />
                </div>
              </div>
            )}

            <div className="rqd-compose-actions">
              {isActive && (
                <button
                  className="rqd-btn rqd-btn-ghost rqd-resolve-link"
                  onClick={() => resolveMutation.mutate()}
                  disabled={resolveMutation.isPending}
                  title={t('detail.resolve')}
                >
                  {resolveMutation.isPending ? t('detail.resolving') : t('detail.resolve')}
                </button>
              )}
              <button
                className="rqd-btn rqd-btn-primary rqd-compose-send"
                onClick={handleSendReply}
                disabled={!replyBody.trim() || replyMutation.isPending}
              >
                {replyMutation.isPending ? t('detail.sending') : t('detail.sendReply')}
              </button>
            </div>
          </div>
        </FormErrorProvider>
      </div>

      {/* ─── Sidebar: only rendered in expanded/two-column mode via CSS ─── */}
      <aside className="rqd-detail-sidebar">
        <div className="rqd-sidebar-section">
          <div className="rqd-sidebar-label">{t('detail.status') ?? 'Status'}</div>
          <div className="rqd-sidebar-value"><span className="rqd-badge">{ticket.status}</span></div>
        </div>
        <div className="rqd-sidebar-section">
          <div className="rqd-sidebar-label">{t('detail.priority') ?? 'Priority'}</div>
          <div className="rqd-sidebar-value"><span className="rqd-badge rqd-badge-muted">{ticket.priority}</span></div>
        </div>
        <div className="rqd-sidebar-section">
          <div className="rqd-sidebar-label">{t('detail.created') ?? 'Created'}</div>
          <div className="rqd-sidebar-value rqd-sidebar-text">{formatDate(ticket.createdAt)}</div>
        </div>
        <div className="rqd-sidebar-section">
          <div className="rqd-sidebar-label">{t('detail.reference') ?? 'Reference'}</div>
          <div className="rqd-sidebar-value rqd-sidebar-text rqd-sidebar-mono">{ticket.ticketNumber}</div>
        </div>

        {ticket.attachments.length > 0 && (
          <div className="rqd-sidebar-section rqd-sidebar-attachments">
            <div className="rqd-sidebar-label">{t('detail.attachments')} ({ticket.attachments.length})</div>
            <div className="rqd-attachment-list">
              {ticket.attachments.map((a: AttachmentMeta) => (
                <div key={a.id} className="rqd-attachment-item">
                  <div className="rqd-file-item-info">
                    <span className="rqd-file-item-name">{a.fileName}</span>
                    <span className="rqd-file-item-size">{formatFileSize(a.fileSize)}</span>
                  </div>
                  {a.downloadUrl && (
                    <a href={a.downloadUrl} target="_blank" rel="noopener noreferrer">
                      {t('attach.download')}
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {isActive && (
          <div className="rqd-sidebar-section">
            <button
              className="rqd-btn rqd-btn-secondary"
              style={{ width: '100%' }}
              onClick={() => resolveMutation.mutate()}
              disabled={resolveMutation.isPending}
            >
              {resolveMutation.isPending ? t('detail.resolving') : t('detail.resolve')}
            </button>
          </div>
        )}
      </aside>
    </div>
  )
}
